import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

const ToastContext = createContext(null);

let toastId = 0;

/**
 * App-wide toast notifications: success / error / info / loading.
 * Use: const toast = useToast(); toast.success('Saved'); toast.loading('Saving…');
 */
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());

  const dismiss = useCallback((id) => {
    const t = timersRef.current.get(id);
    if (t) {
      clearTimeout(t);
      timersRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const push = useCallback(
    (message, { type = 'info', duration = 3200, id: existingId } = {}) => {
      const id = existingId ?? `toast-${++toastId}`;
      setToasts((prev) => {
        const without = prev.filter((x) => x.id !== id);
        return [...without, { id, message: String(message || ''), type }];
      });
      const prevTimer = timersRef.current.get(id);
      if (prevTimer) clearTimeout(prevTimer);
      if (type !== 'loading' && duration > 0) {
        timersRef.current.set(
          id,
          setTimeout(() => dismiss(id), duration),
        );
      }
      return id;
    },
    [dismiss],
  );

  const api = useMemo(
    () => ({
      show: (message, opts) => push(message, opts),
      success: (message, opts) => push(message, { duration: 2800, ...opts, type: 'success' }),
      error: (message, opts) => push(message, { duration: 4500, ...opts, type: 'error' }),
      info: (message, opts) => push(message, { duration: 3200, ...opts, type: 'info' }),
      loading: (message, opts) => push(message, { duration: 0, ...opts, type: 'loading' }),
      dismiss,
      /** Replace a loading toast with success/error and auto-dismiss. */
      update: (id, message, type = 'success') =>
        push(message, { id, type, duration: type === 'error' ? 4500 : 2800 }),
    }),
    [push, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="app-toast-host" aria-live="polite" aria-relevant="additions text">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`app-toast app-toast--${t.type}`}
            role={t.type === 'error' ? 'alert' : 'status'}
          >
            <span className="app-toast__icon" aria-hidden="true">
              {t.type === 'success' ? '✓' : t.type === 'error' ? '!' : t.type === 'loading' ? '…' : 'i'}
            </span>
            <span className="app-toast__msg">{t.message}</span>
            {t.type !== 'loading' && (
              <button
                type="button"
                className="app-toast__close"
                onClick={() => dismiss(t.id)}
                aria-label="Dismiss"
              >
                ×
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Safe no-op outside provider (tests / isolated renders)
    return {
      show: () => null,
      success: () => null,
      error: () => null,
      info: () => null,
      loading: () => null,
      dismiss: () => {},
      update: () => null,
    };
  }
  return ctx;
}
